import * as React from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import DialogActions from "@mui/joy/DialogActions";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ColorSchemeToggle from "./Utils/ColorSchemeToggle";
import { closeSidebar } from "../utils";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import GolfCourseIcon from "@mui/icons-material/GolfCourse";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import Badge, { badgeClasses } from "@mui/joy/Badge";
import InfoIcon from "@mui/icons-material/Info";
import ReportIcon from "@mui/icons-material/Report";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { db } from "../FirebaseConfig";
import { toast } from "react-toastify";
import StadiumIcon from "@mui/icons-material/Stadium";
import SchoolIcon from "@mui/icons-material/School";

type EventData = {
  eventname: string;
  id: string;
};

type RegistrationData = {
  userid: string;
};

function generateSlug(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
}

function Toggler({
  defaultExpanded = false,
  renderToggle,
  children,
}: Readonly<{
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}>) {
  const [open, setOpen] = React.useState(defaultExpanded);
  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "0.2s ease",
          "& > *": {
            overflow: "hidden",
          },
        }}
      >
        {children}
      </Box>
    </React.Fragment>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { logout, userData } = UserAuth();
  const [open, setOpen] = React.useState(false);
  const [events, setEvents] = React.useState<EventData[]>([]);
  const [registeredEvents, setRegisteredEvents] = React.useState<string[]>([]);
  const navigate = useNavigate();

  const logOutWeb = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await logout();
      navigate("/"); // Redirect using react-router
    } catch (error) {
      toast.error("Error during logout.");
    }
  };  

  const fetchEvents = React.useCallback(() => {
    const unsubscribe = db
      .collection("Events")
      .where("live", "==", true)
      .onSnapshot((querySnapshot) => {
        const eventsData = querySnapshot.docs.map((doc) => ({
          eventname: doc.data().eventname,
          id: doc.id,
        }));
        setEvents(eventsData);
      });
  
    return unsubscribe;
  }, []);
  
  React.useEffect(() => {
    const unsubscribe = fetchEvents();
    return () => unsubscribe(); // Clean up listener when component unmounts
  }, [fetchEvents]);
  

  React.useEffect(() => {
    if (!userData?.id || events.length === 0) return;
  
    const unsubscribers: (() => void)[] = [];
    let userRegisteredEventsSet = new Set<string>(); // Use Set for faster lookup
  
    events.forEach((event) => {
      const unsubscribe = db
        .collection("Events")
        .doc(event.id)
        .collection("Registrations")
        .onSnapshot((snapshot) => {
          const isRegistered = snapshot.docs.some((doc) => {
            const registrationData = doc.data() as RegistrationData;
            return registrationData.userid === userData?.id;
          });
  
          if (isRegistered) {
            userRegisteredEventsSet.add(event.eventname); // Add to Set if registered
          } else {
            userRegisteredEventsSet.delete(event.eventname); // Remove from Set if not registered
          }
  
          setRegisteredEvents(Array.from(userRegisteredEventsSet)); // Convert Set to Array and set state
        });
  
      unsubscribers.push(unsubscribe);
    });
  
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [events, userData]);  

  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", md: "sticky" },
        transform: {
          xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
          md: "none",
        },
        transition: "transform 0.4s, width 0.4s",
        zIndex: 10000,
        height: "100dvh",
        width: "var(--Sidebar-width)",
        top: 0,
        p: 2,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ":root": {
            "--Sidebar-width": "220px",
            [theme.breakpoints.up("lg")]: {
              "--Sidebar-width": "240px",
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 9998,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          backgroundColor: "var(--joy-palette-background-backdrop)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
            lg: "translateX(-100%)",
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <IconButton size="sm">
          <img
            src={process.env.REACT_APP_LOGO}
            alt="Icon"
            height="33px"
            width="33px"
          />
        </IconButton>
        <Typography level="title-lg">{process.env.REACT_APP_NAME}</Typography>
        <ColorSchemeToggle sx={{ ml: "auto" }} />
      </Box>
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          aria-label="Sidebar"
          size="sm"
          sx={{
            gap: 1,
            "--List-nestedInsetStart": "30px",
            "--ListItem-radius": (theme) => theme.vars.radius.sm,
            '& [role="button"]': {
              borderRadius: "0 20px 20px 0",
            },
          }}
        >
          <ListItem key="dashboard">
            <ListItemButton
              selected={location.pathname === "/dashboard"}
              color={location.pathname === "/dashboard" ? "primary" : undefined}
              role="menuitem"
              component={RouterLink}
              to="/dashboard"
            >
              <DashboardRoundedIcon />
              <ListItemContent>
                <Typography level="title-sm">Dashboard</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem key="challenges">
            <ListItemButton
              selected={location.pathname === "/challenges"}
              color={
                location.pathname === "/challenges" ? "primary" : undefined
              }
              role="menuitem"
              component={RouterLink}
              to="/challenges"
            >
              <GolfCourseIcon />
              <ListItemContent>
                <Typography level="title-sm">Challenges</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem key="leaderboard">
            <ListItemButton
              selected={location.pathname === "/leaderboard"}
              color={
                location.pathname === "/leaderboard" ? "primary" : undefined
              }
              role="menuitem"
              component={RouterLink}
              to="/leaderboard"
            >
              <EmojiEventsIcon />
              <ListItemContent>
                <Typography level="title-sm">Leaderboard</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem key="learning">
            <ListItemButton
              selected={location.pathname === "/learning"}
              color={location.pathname === "/learning" ? "primary" : undefined}
              role="menuitem"
              component={RouterLink}
              to="https://academy.hacktheway.com"
            >
              <SchoolIcon />
              <ListItemContent>
                <Typography level="title-sm">Learn</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>

          <ListItem nested key="report">
            <Toggler
              renderToggle={({ open, setOpen }) => (
                <ListItemButton onClick={() => setOpen(!open)}>
                  <ReportIcon />
                  <ListItemContent>
                    <Typography level="title-sm">Report</Typography>
                  </ListItemContent>
                  <KeyboardArrowDownIcon
                    sx={{ transform: open ? "rotate(180deg)" : "none" }}
                  />
                </ListItemButton>
              )}
            >
              <List sx={{ gap: 0.5 }}>
                <ListItem key="myreport">
                  <ListItemButton
                    selected={location.pathname === "/myreport"}
                    color={
                      location.pathname === "/myreport" ? "primary" : undefined
                    }
                    role="menuitem"
                    component={RouterLink}
                    to="/myreport"
                  >
                    My Report
                  </ListItemButton>
                </ListItem>
                {(userData?.role == "organizer" ||
                  userData?.role === "admin" ||
                  userData?.role === "super") && (
                  <ListItem key="evaluateReport">
                    <ListItemButton
                      selected={location.pathname === "/eveluatereport"}
                      color={
                        location.pathname === "evaluatereport"
                          ? "primary"
                          : undefined
                      }
                      role="menuitem"
                      component={RouterLink}
                      to="/evaluatereport"
                    >
                      Evaluate Reports
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Toggler>
          </ListItem>
          {registeredEvents.length > 0 && (
            <ListItem nested key="myEvents">
              <Toggler
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <StadiumIcon />
                    <ListItemContent>
                      <Typography level="title-sm">My Events</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={{ transform: open ? "rotate(180deg)" : "none" }}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                  {events
                    .filter((event) =>
                      registeredEvents.includes(event.eventname)
                    )
                    .map((event) => (
                      <ListItem key={event.id}>
                        <ListItemButton
                          selected={
                            location.pathname ===
                            `/event/${generateSlug(event.id)}`
                          }
                          color={
                            location.pathname ===
                            `/event/${generateSlug(event.id)}`
                              ? "primary"
                              : undefined
                          }
                          role="menuitem"
                          component={RouterLink}
                          to={`/event/${generateSlug(event.id)}`}
                        >
                          {event.eventname}
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Toggler>
            </ListItem>
          )}
        </List>
        <List
          size="sm"
          sx={{
            mt: "auto",
            flexGrow: 0,
            gap: 1,
            "--ListItem-radius": (theme) => theme.vars.radius.sm,
            '& [role="button"]': {
              borderRadius: "0 20px 20px 0",
            },
          }}
        >
          {(userData?.role === "admin" || userData?.role === "super" || userData?.role === "organizer" ) && (
            <ListItem nested>
              <Toggler
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <AdminPanelSettingsIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Admin Panel</Typography>
                    </ListItemContent>
                    <KeyboardArrowUpIcon
                      sx={{ transform: open ? "rotate(180deg)" : "none" }}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                {(userData?.role === "admin" || userData?.role === "super" ) && (
                  <ListItem>
                    <ListItemButton
                      selected={location.pathname === "/addchallenge"}
                      color={
                        location.pathname === "/addchallenge"
                          ? "primary"
                          : undefined
                      }
                      role="menuitem"
                      component={RouterLink}
                      to="/addchallenge"
                    >
                      Add Challenge
                    </ListItemButton>
                  </ListItem>
                )}
                  <ListItem>
                    <ListItemButton
                      selected={location.pathname === "/addevent"}
                      color={
                        location.pathname === "/addevent"
                          ? "primary"
                          : undefined
                      }
                      role="menuitem"
                      component={RouterLink}
                      to="/addevent"
                    >
                      Add Event
                    </ListItemButton>
                  </ListItem>
                  {(userData?.role === "admin" || userData?.role === "super") && (
                  <ListItem>
                    <ListItemButton
                      selected={location.pathname === "/managechallenge"}
                      color={
                        location.pathname === "/managechallenge"
                          ? "primary"
                          : undefined
                      }
                      role="menuitem"
                      component={RouterLink}
                      to="/managechallenge"
                    >
                      Manage Challenges
                    </ListItemButton>
                  </ListItem>
                  )}
                  <ListItem>
                    <ListItemButton
                      selected={location.pathname === "/manageevent"}
                      color={
                        location.pathname === "/manageevent"
                          ? "primary"
                          : undefined
                      }
                      role="menuitem"
                      component={RouterLink}
                      to="/manageevent"
                    >
                      Manage Events
                    </ListItemButton>
                  </ListItem>
                  {userData?.role == "super" && (
                    <ListItem>
                      <ListItemButton
                        selected={location.pathname === "/manageuser"}
                        color={
                          location.pathname === "/manageuser"
                            ? "primary"
                            : undefined
                        }
                        role="menuitem"
                        component={RouterLink}
                        to="/manageuser"
                      >
                        Manage Users
                      </ListItemButton>
                    </ListItem>
                  )}
                </List>
              </Toggler>
            </ListItem>
          )}
          <ListItem>
            <ListItemButton
              selected={location.pathname === "/aboutus"}
              color={location.pathname === "/aboutus" ? "primary" : undefined}
              role="menuitem"
              component={RouterLink}
              to="/aboutus"
            >
              <InfoIcon />
              About us
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === "/settings"}
              color={location.pathname === "/settings" ? "primary" : undefined}
              role="menuitem"
              component={RouterLink}
              to="/settings"
            >
              <SettingsRoundedIcon />
              Settings
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Divider />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Badge
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeInset="14%"
          color="success"
          sx={{
            position: "relative",
            [`& .${badgeClasses.badge}`]: {
              "&::after": {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                animation: "ripple 1.2s infinite ease-in-out",
                border: "2px solid",
                borderColor: "success.500",
                content: '""',
              },
            },
            "@keyframes ripple": {
              "0%": {
                transform: "scale(1)",
                opacity: 1,
              },
              "100%": {
                transform: "scale(2)",
                opacity: 0,
              },
            },
          }}
        >
          <Avatar
            variant="outlined"
            size="sm"
            src={userData?.profilePic ?? ""}
          />
        </Badge>
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            mt: 0.4,
            ml: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Typography level="title-lg">
            {userData?.name?.split(" ")[0]}{" "}
          </Typography>
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <LogoutRoundedIcon />
        </IconButton>
      </Box>
      <div>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>
              <WarningRoundedIcon />
              Confirmation
            </DialogTitle>
            <Divider />
            <DialogContent>Are you sure you want to logout ?</DialogContent>
            <DialogActions>
              <Button variant="plain" color="danger" onClick={logOutWeb}>
                Logout
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </div>
    </Sheet>
  );
}
